import { FunctionComponent } from 'react';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import InfoIcon from '@mui/icons-material/Info';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import styles from './ConsentCheckboxes.module.css';

interface ConsentItem {
  id: string;
  label: string;
  description?: string;
  required: boolean;
  infoLink?: {
    text: string;
    url: string;
  };
}

interface ConsentCheckboxesProps {
  title?: string;
  items: ConsentItem[];
  value: Record<string, boolean>;
  onChange: (consents: Record<string, boolean>) => void;
  showDescription?: boolean;
  layout?: 'vertical' | 'compact';
  hasNextButton?: boolean;
  onNext?: () => void;
}

const ConsentCheckboxes: FunctionComponent<ConsentCheckboxesProps> = ({
  title = 'Consent & Authorization',
  items,
  value,
  onChange,
  showDescription = true,
  layout = 'vertical',
  hasNextButton = false,
  onNext
}) => {
  const handleToggle = (itemId: string) => {
    onChange({
      ...value,
      [itemId]: !value[itemId]
    });
  };

  const allRequiredChecked = items
    .filter(item => item.required)
    .every(item => value[item.id]);

  return (
    <div className={styles.container}>
      {title && <h3 className={styles.title}>{title}</h3>}
      
      <div className={`${styles.consentList} ${styles[layout]}`}>
        {items.map(item => (
          <div 
            key={item.id} 
            className={`${styles.consentItem} ${value[item.id] ? styles.checked : ''}`}
          >
            <div className={styles.checkboxWrapper}>
              <button
                type="button"
                className={styles.checkbox}
                onClick={() => handleToggle(item.id)}
                aria-label={`${value[item.id] ? 'Uncheck' : 'Check'} ${item.label}`}
                aria-pressed={value[item.id]}
              >
                {value[item.id] ? (
                  <CheckCircleIcon className={styles.checkIcon} />
                ) : (
                  <CheckCircleOutlineIcon className={styles.uncheckIcon} />
                )}
              </button>
              
              <div className={styles.content}>
                <label className={styles.label} onClick={() => handleToggle(item.id)}>
                  {item.label}
                  {item.required && <span className={styles.required}>*</span>}
                </label>
                
                {showDescription && item.description && (
                  <p className={styles.description}>{item.description}</p>
                )}
                
                {item.infoLink && (
                  <a 
                    href={item.infoLink.url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className={styles.infoLink}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <InfoIcon className={styles.infoIcon} />
                    {item.infoLink.text}
                  </a>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {!allRequiredChecked && (
        <div className={styles.requiredNotice}>
          <InfoIcon className={styles.noticeIcon} />
          <span>Please check all required items to continue</span>
        </div>
      )}
      
      {hasNextButton && (
        <button 
          type="button" 
          className={styles.nextButton} 
          onClick={onNext}
          disabled={!allRequiredChecked}
        >
          Next
          <ChevronRightIcon />
        </button>
      )}
    </div>
  );
};

// Common consent presets
export const commonConsentItems: ConsentItem[] = [
  {
    id: 'hipaa',
    label: 'I authorize the sharing of my health information',
    description: 'I understand that my health information will be shared with my selected healthcare providers in accordance with HIPAA regulations.',
    required: true,
    infoLink: {
      text: 'Learn about HIPAA',
      url: 'https://www.hhs.gov/hipaa/for-patients/index.html'
    }
  },
  {
    id: 'telehealth',
    label: 'I consent to telehealth services',
    description: 'I agree to receive healthcare services via video consultation when appropriate and understand the limitations of remote care.',
    required: false
  },
  {
    id: 'communication',
    label: 'I consent to receive appointment reminders',
    description: 'I agree to receive appointment reminders and health information via email, SMS, and phone calls.',
    required: false
  },
  {
    id: 'billing',
    label: 'I acknowledge financial responsibility',
    description: 'I understand that I am financially responsible for any costs not covered by my insurance.',
    required: true
  },
  {
    id: 'research',
    label: 'I consent to anonymous data use for research',
    description: 'I agree that my anonymized health data may be used for medical research and improving healthcare services.',
    required: false,
    infoLink: {
      text: 'Privacy Policy',
      url: '/privacy-policy'
    }
  }
];

export default ConsentCheckboxes;